import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { Seo } from "../components/Seo";
import { PLATFORMS } from "../data/platforms";

const SCIENTIFIC = PLATFORMS.filter((p) => p.slug !== "foresightrx");

function Logo({ p }) {
  if (p.wordmark) return <img src={p.logo} alt={`${p.name} logo`} className="h-9 w-auto max-w-[220px]" loading="lazy" />;
  return (
    <span className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl border border-slate-200 shadow-sm" style={{ background: p.logoBg || "#fff" }}>
      <img src={p.logo} alt={`${p.name} logo`} className={p.logoBg ? "h-8 w-8" : "h-full w-full object-cover"} loading="lazy" />
    </span>
  );
}

export default function Platforms() {
  return (
    <div data-testid="platforms-page">
      <Seo title="AI-Enabled Platforms" description="Explore MarksMan Healthcare's AI-enabled platforms for patient insights, evidence planning, medical affairs, market access and biopharma partnering." />

      <section className="border-b border-slate-200 bg-[var(--surface)]">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="max-w-3xl">
            <div className="eyebrow">Our Platforms</div>
            <h1 className="mt-4 text-4xl font-extrabold leading-[1.1] text-[var(--navy)] sm:text-5xl">Scientific expertise. Connected intelligence.</h1>
            <p className="mt-6 text-lg leading-relaxed text-slate-600">MarksMan Healthcare combines scientific expertise with proprietary AI-enabled platforms to support patient and clinician insights, evidence planning, medical affairs, market access and biopharma partnering. Our platforms strengthen expert-led engagements through tailored workflows, human review and source traceability.</p>
            <p className="mt-4 text-base text-slate-600">Explore our platforms below, or speak with us about the evidence and business questions your team needs to address.</p>
            <div className="mt-8"><Link to="/contact" data-testid="platforms-cta-top" className="btn-primary">Discuss Your Requirements <ArrowRight size={15} /></Link></div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" aria-labelledby="platforms-grid-heading">
        <h2 id="platforms-grid-heading" className="sr-only">Our platforms</h2>
        <ol className="space-y-4" data-testid="platforms-grid">
          {SCIENTIFIC.map((p, i) => (
            <li key={p.slug} data-testid={`platform-card-${p.slug}`} className="card grid gap-6 !p-6 sm:!p-8 lg:grid-cols-12 lg:items-center">
              <div className="flex items-start gap-5 lg:col-span-5">
                <span className="hidden font-mono text-xs font-bold text-slate-300 sm:block">0{i + 1}</span>
                <div className="min-w-0">
                  <Logo p={p} />
                  <div className="eyebrow mt-4">{p.category}</div>
                  <h3 className="mt-2 text-xl font-bold text-[var(--navy)] sm:text-2xl">{p.name}</h3>
                  <p className="mt-1 text-sm font-semibold text-[var(--teal)]">{p.subtitle}</p>
                </div>
              </div>
              <div className="flex flex-col lg:col-span-7 lg:border-l lg:border-slate-100 lg:pl-8">
                <p className="text-sm leading-relaxed text-slate-600 sm:text-base">{p.description}</p>
                <a href={p.url} target="_blank" rel="noopener noreferrer" data-testid={`platform-link-${p.slug}`} className="mt-5 inline-flex items-center gap-1.5 self-start text-sm font-semibold text-[var(--navy)] transition-colors duration-200 hover:text-[var(--teal)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--teal)]">
                  Explore Platform <ExternalLink size={14} aria-hidden="true" /><span className="sr-only">(opens in a new tab)</span>
                </a>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="border-y border-slate-200 bg-[var(--surface)]" data-testid="platforms-services">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--navy)] sm:text-3xl lg:col-span-4">From insight to scientific delivery</h2>
          <p className="text-base leading-relaxed text-slate-600 lg:col-span-8">Our platforms support the identification of evidence needs and strategic priorities. MarksMan's teams can take these forward through evidence synthesis, real-world evidence, HEOR, biostatistics, market-access support and scientific communications, with the scope tailored to each engagement.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="platforms-contact">
        <div className="grain relative overflow-hidden rounded-2xl bg-[var(--navy)] px-8 py-12 text-white sm:px-14">
          <div className="relative grid gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-8">
              <h2 className="text-2xl font-bold sm:text-3xl">Have a specific question or project in mind?</h2>
              <p className="mt-3 text-base text-slate-300">Tell us about your objectives. We can help identify the relevant platform, scientific expertise and scope of support for your team.</p>
            </div>
            <div className="lg:col-span-4 lg:text-right"><Link to="/contact" data-testid="platforms-cta-bottom" className="btn-primary">Discuss Your Requirements <ArrowRight size={15} /></Link></div>
          </div>
        </div>
      </section>
    </div>
  );
}
